import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { 
  UserPlus, 
  RefreshCw, 
  Mail, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle,
  Eye,
  Search,
  Calendar
} from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';

interface DirectLead {
  id: string;
  user_id: string;
  external_contact_id: string;
  location_id: string;
  source: string;
  raw_payload: any;
  parsed_data: any;
  status: 'received' | 'processing' | 'sent_to_4d' | 'failed' | 'retry';
  error_message: string | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
  sent_to_4d_at: string | null;
}

interface EmailLead {
  id: string;
  user_id: string;
  type: 'lead_new';
  payload: any;
  status: 'received' | 'processed' | 'error';
  error: string | null;
  created_at: string;
  processed_at: string | null;
}

interface ContactInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  birthDate?: string;
  note?: string;
}

interface UnifiedLead {
  id: string;
  source: 'Email' | 'Direct';
  status: string;
  created_at: string;
  processed_at: string | null;
  error_message: string | null;
  retry_count?: number;
  contact_info: ContactInfo;
  raw_data: any;
  parsed_data?: any;
}

const getFirstDefined = (...values: Array<string | null | undefined>) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return undefined;
};

const normalizeContactInfo = (parsed: any, raw: any): ContactInfo => {
  const rawContact = raw?.contact || raw;
  const customData = typeof parsed?.CustomData === 'string' ? safeJsonParse(parsed.CustomData) : parsed?.CustomData;

  return {
    firstName: getFirstDefined(
      parsed?.firstName,
      parsed?.FirstName,
      rawContact?.firstName,
      rawContact?.FirstName,
      customData?.originalPayload?.firstName,
      customData?.originalPayload?.FirstName
    ),
    lastName: getFirstDefined(
      parsed?.lastName,
      parsed?.LastName,
      rawContact?.lastName,
      rawContact?.LastName,
      customData?.originalPayload?.lastName,
      customData?.originalPayload?.LastName
    ),
    email: getFirstDefined(
      parsed?.email,
      parsed?.Email,
      rawContact?.email,
      rawContact?.Email,
      customData?.originalPayload?.email,
      customData?.originalPayload?.Email
    ),
    phone: getFirstDefined(
      parsed?.phone,
      parsed?.Phone,
      rawContact?.phone,
      rawContact?.Phone,
      customData?.originalPayload?.phone,
      customData?.originalPayload?.Phone
    ),
    birthDate: getFirstDefined(
      parsed?.birthDate,
      parsed?.BirthDate,
      rawContact?.birthDate,
      rawContact?.BirthDate,
      customData?.originalPayload?.birthDate,
      customData?.originalPayload?.BirthDate
    ),
    note: getFirstDefined(
      parsed?.note,
      parsed?.Note,
      rawContact?.note,
      rawContact?.Note,
      customData?.originalPayload?.note,
      customData?.originalPayload?.Note
    ),
  };
};

function safeJsonParse(value: string | undefined) {
  if (!value) return undefined;
  try {
    return JSON.parse(value);
  } catch (_error) {
    return undefined;
  }
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

export default function LeadsTo4D() {
  const [selectedLead, setSelectedLead] = useState<UnifiedLead | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();

  // Build status filter for direct leads query
  const getDirectStatusFilter = (status: string) => {
    switch (status) {
      case 'sent_to_4d': return ['sent_to_4d'];
      case 'processing': return ['processing', 'received'];
      case 'failed': return ['failed'];
      case 'retry': return ['retry'];
      default: return null;
    }
  };

  // Fetch direct leads from highlevel_leads table with pagination
  const { data: directLeadsData, isLoading: isLoadingDirect } = useQuery({
    queryKey: ['highlevel-leads', currentPage, pageSize, statusFilter, sourceFilter, searchQuery],
    queryFn: async () => {
      // If filtering by email source only, skip direct leads
      if (sourceFilter === 'email') {
        return { leads: [], count: 0 };
      }

      let query = supabase
        .from('highlevel_leads')
        .select('*', { count: 'exact' });

      // Apply status filter
      const statusValues = getDirectStatusFilter(statusFilter);
      if (statusValues) {
        query = query.in('status', statusValues);
      }

      // Apply search filter (search in parsed_data or raw_payload)
      if (searchQuery.trim()) {
        query = query.or(`parsed_data->>email.ilike.%${searchQuery}%,parsed_data->>firstName.ilike.%${searchQuery}%,parsed_data->>lastName.ilike.%${searchQuery}%`);
      }

      query = query.order('created_at', { ascending: false });

      // If both sources, we need to split pagination
      if (sourceFilter === 'all') {
        // Get count first
        const { count, error: countError } = await query;
        if (countError) throw countError;
        
        // Get paginated data
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize - 1;
        const { data, error } = await query.range(start, end);
        
        if (error) throw error;
        return { leads: data as DirectLead[], count: count || 0 };
      } else {
        // Direct only - full pagination
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize - 1;
        const { data, count, error } = await query.range(start, end);
        
        if (error) throw error;
        return { leads: data as DirectLead[], count: count || 0 };
      }
    },
  });

  // Fetch email leads from emr_transactions table with pagination
  const { data: emailLeadsData, isLoading: isLoadingEmail } = useQuery({
    queryKey: ['email-leads', currentPage, pageSize, statusFilter, sourceFilter, searchQuery],
    queryFn: async () => {
      // If filtering by direct source only, skip email leads
      if (sourceFilter === 'direct') {
        return { leads: [], count: 0 };
      }

      // Map our status filter to email lead statuses
      const getEmailStatusFilter = (status: string) => {
        switch (status) {
          case 'sent_to_4d': return ['processed'];
          case 'failed': return ['error'];
          case 'processing': return ['received'];
          default: return null;
        }
      };

      let query = supabase
        .from('emr_transactions')
        .select('*', { count: 'exact' })
        .eq('type', 'lead_new');

      // Apply status filter
      const statusValues = getEmailStatusFilter(statusFilter);
      if (statusValues) {
        query = query.in('status', statusValues);
      }

      // Apply search filter
      if (searchQuery.trim()) {
        query = query.or(`payload->>email.ilike.%${searchQuery}%,payload->>firstName.ilike.%${searchQuery}%,payload->>lastName.ilike.%${searchQuery}%`);
      }

      query = query.order('created_at', { ascending: false });

      // Get paginated data
      const start = (currentPage - 1) * pageSize;
      const end = start + pageSize - 1;
      let { data, count, error } = await query.range(start, end);

      // Fallback to old table name if new doesn't exist
      if (error && (error.code === '42P01' || error.message?.includes('does not exist'))) {
        console.log('emr_transactions not found, falling back to fourdeemr_transactions...');
        let fallbackQuery = supabase
          .from('fourdeemr_transactions')
          .select('*', { count: 'exact' })
          .eq('type', 'lead_new');

        if (statusValues) {
          fallbackQuery = fallbackQuery.in('status', statusValues);
        }

        if (searchQuery.trim()) {
          fallbackQuery = fallbackQuery.or(`payload->>email.ilike.%${searchQuery}%,payload->>firstName.ilike.%${searchQuery}%,payload->>lastName.ilike.%${searchQuery}%`);
        }

        fallbackQuery = fallbackQuery.order('created_at', { ascending: false }).range(start, end);

        const fallbackResult = await fallbackQuery;
        data = fallbackResult.data;
        count = fallbackResult.count;
        error = fallbackResult.error;
      }

      if (error) throw error;
      return { leads: data as EmailLead[], count: count || 0 };
    },
  });

  // Process leads mutation
  const processLeadsMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch('/.netlify/functions/process-highlevel-leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errorMessage = responseData.message || responseData.error || response.statusText;
        throw new Error(`Processing failed: ${errorMessage}`);
      }

      return responseData;
    },
    onSuccess: (data) => {
      const message = data.message || 'Lead processing completed';
      const processed = data.processed || 0;
      const successful = data.successful || 0;
      const failed = data.failed || 0;
      
      if (processed === 0) {
        toast.success('No leads to process');
      } else {
        toast.success(`${message} - Processed: ${processed}, Successful: ${successful}, Failed: ${failed}`);
      }
      queryClient.invalidateQueries({ queryKey: ['highlevel-leads'] });
      queryClient.invalidateQueries({ queryKey: ['email-leads'] });
    },
    onError: (error: any) => {
      toast.error(`Failed to process leads: ${error.message}`);
    },
  });

  // Extract leads and counts
  const directLeads = directLeadsData?.leads || [];
  const directLeadsCount = directLeadsData?.count || 0;
  const emailLeads = emailLeadsData?.leads || [];
  const emailLeadsCount = emailLeadsData?.count || 0;

  // Total count for pagination
  const totalCount = directLeadsCount + emailLeadsCount;
  const totalPages = Math.ceil(totalCount / pageSize);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, sourceFilter, searchQuery, pageSize]);

  // Combine and transform leads
  const unifiedLeads: UnifiedLead[] = React.useMemo(() => {
    const combined: UnifiedLead[] = [];

    // Add direct leads
    if (directLeads) {
      directLeads.forEach(lead => {
        const contactInfo = normalizeContactInfo(lead.parsed_data, lead.raw_payload);

        combined.push({
          id: lead.id,
          source: 'Direct',
          status: lead.status,
          created_at: lead.created_at,
          processed_at: lead.sent_to_4d_at,
          error_message: lead.error_message,
          retry_count: lead.retry_count,
          contact_info: contactInfo,
          raw_data: lead.raw_payload,
          parsed_data: lead.parsed_data,
        });
      });
    }

    // Add email leads
    if (emailLeads) {
      emailLeads.forEach(lead => {
        const payload = lead.payload;
        const contactInfo = normalizeContactInfo(payload, payload);

        combined.push({
          id: lead.id,
          source: 'Email',
          status: lead.status === 'processed' ? 'sent_to_4d' : lead.status === 'error' ? 'failed' : 'received',
          created_at: lead.created_at,
          processed_at: lead.processed_at,
          error_message: lead.error,
          contact_info: contactInfo,
          raw_data: payload,
        });
      });
    }

    return combined.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [directLeads, emailLeads]);

  const isLoading = isLoadingDirect || isLoadingEmail;

  // Calculate status summary
  const statusCounts = unifiedLeads.reduce((acc, lead) => {
    acc[lead.status] = (acc[lead.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent_to_4d':
      case 'processed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'processing':
        return <Clock className="w-4 h-4 text-blue-500" />;
      case 'failed':
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'retry':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (status) {
      case 'sent_to_4d':
      case 'processed':
        return <span className={`${baseClasses} bg-green-100 text-green-800`}>Sent to 4D</span>;
      case 'processing':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>Processing</span>;
      case 'failed':
      case 'error':
        return <span className={`${baseClasses} bg-red-100 text-red-800`}>Failed</span>;
      case 'retry':
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Retry</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Received</span>;
    }
  };

  const getSourceBadge = (source: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    if (source === 'Email') {
      return (
        <span className={`${baseClasses} bg-indigo-100 text-indigo-800`}>
          <Mail className="w-3 h-3 mr-1" /> Email
        </span>
      );
    } else {
      return (
        <span className={`${baseClasses} bg-purple-100 text-purple-800`}>
          <UserPlus className="w-3 h-3 mr-1" /> Direct
        </span>
      );
    }
  };

  if (selectedLead) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Lead Details</h1>
            <p className="text-sm text-gray-600">
              {selectedLead.source} lead from {format(new Date(selectedLead.created_at), 'PPp')}
            </p>
          </div>
          <button
            onClick={() => setSelectedLead(null)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            ← Back to Leads
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Contact Information */}
          <Card>
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2 mb-4">
                <UserPlus className="w-5 h-5" />
                Contact Information
              </h3>
              <div className="space-y-4">
                {selectedLead.contact_info.firstName && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Name:</span>
                    <span>{selectedLead.contact_info.firstName} {selectedLead.contact_info.lastName}</span>
                  </div>
                )}
                {selectedLead.contact_info.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <span>{selectedLead.contact_info.email}</span>
                  </div>
                )}
                {selectedLead.contact_info.phone && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <span>{selectedLead.contact_info.phone}</span>
                  </div>
                )}
                {selectedLead.contact_info.birthDate && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span>{selectedLead.contact_info.birthDate}</span>
                  </div>
                )}
                {selectedLead.contact_info.note && (
                  <div className="space-y-1">
                    <span className="font-medium">Note:</span>
                    <p className="text-sm text-gray-600">{selectedLead.contact_info.note}</p>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Lead Status */}
          <Card>
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2 mb-4">
                {getStatusIcon(selectedLead.status)}
                Lead Status
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Status:</span>
                  {getStatusBadge(selectedLead.status)}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Source:</span>
                  {getSourceBadge(selectedLead.source)}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Created:</span>
                  <span>{format(new Date(selectedLead.created_at), 'PPp')}</span>
                </div>
                {selectedLead.processed_at && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Processed:</span>
                    <span>{format(new Date(selectedLead.processed_at), 'PPp')}</span>
                  </div>
                )}
                {selectedLead.retry_count !== undefined && selectedLead.retry_count > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Retries:</span>
                    <span>{selectedLead.retry_count}</span>
                  </div>
                )}
                {selectedLead.error_message && (
                  <div className="space-y-1">
                    <span className="font-medium text-red-600">Error:</span>
                    <p className="text-sm text-red-600 bg-red-50 p-2 rounded">
                      {selectedLead.error_message}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Raw Data */}
          <Card className="lg:col-span-2">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Raw Data</h3>
              <p className="text-sm text-gray-600 mb-4">Original data received from the lead source</p>
              <pre className="bg-gray-50 p-4 rounded-lg text-xs overflow-x-auto">
                {JSON.stringify(selectedLead.raw_data, null, 2)}
              </pre>
            </div>
          </Card>

          {/* Parsed Data (for direct leads only) */}
          {selectedLead.parsed_data && (
            <Card className="lg:col-span-2">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Parsed Data</h3>
                <p className="text-sm text-gray-600 mb-4">Data formatted for 4D EMR</p>
                <pre className="bg-gray-50 p-4 rounded-lg text-xs overflow-x-auto">
                  {JSON.stringify(selectedLead.parsed_data, null, 2)}
                </pre>
              </div>
            </Card>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads to 4D</h1>
          <p className="text-sm text-gray-600">
            Monitor and manage leads sent from email captures and direct webhooks to 4D EMR
          </p>
        </div>
        <button
          onClick={() => processLeadsMutation.mutate()}
          disabled={processLeadsMutation.isPending}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processLeadsMutation.isPending ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Process Leads
        </button>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Leads</p>
                <p className="text-2xl font-bold">{totalCount.toLocaleString()}</p>
              </div>
              <UserPlus className="w-8 h-8 text-gray-400" />
            </div>
          </div>
        </Card>
        
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Sent to 4D</p>
                <p className="text-2xl font-bold text-green-600">
                  {(statusCounts.sent_to_4d || 0) + (statusCounts.processed || 0)}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </div>
        </Card>
        
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Processing</p>
                <p className="text-2xl font-bold text-blue-600">
                  {(statusCounts.processing || 0) + (statusCounts.received || 0)}
                </p>
              </div>
              <Clock className="w-8 h-8 text-blue-400" />
            </div>
          </div>
        </Card>
        
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">Failed</p>
                <p className="text-2xl font-bold text-red-600">
                  {(statusCounts.failed || 0) + (statusCounts.error || 0)}
                </p>
              </div>
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Leads Table */}
      <Card>
        <div className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">All Leads</h3>
              <p className="text-sm text-gray-600">
                Leads from both email captures and direct HighLevel webhooks
              </p>
            </div>
            <div className="text-sm text-gray-500">
              Showing {unifiedLeads.length} of {totalCount.toLocaleString()} leads
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="search" className="block text-xs font-medium text-gray-500 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="search"
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="w-[180px]">
              <label htmlFor="status-filter" className="block text-xs font-medium text-gray-500 mb-1">
                Status
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
              >
                <option value="all">All Statuses</option>
                <option value="sent_to_4d">Sent to 4D</option>
                <option value="processing">Processing</option>
                <option value="failed">Failed</option>
                <option value="retry">Retry</option>
              </select>
            </div>

            {/* Source Filter */}
            <div className="w-[180px]">
              <label htmlFor="source-filter" className="block text-xs font-medium text-gray-500 mb-1">
                Source
              </label>
              <select
                id="source-filter"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
              >
                <option value="all">All Sources</option>
                <option value="direct">Direct (Webhook)</option>
                <option value="email">Email Capture</option>
              </select>
            </div>

            {/* Page Size */}
            <div className="w-[120px]">
              <label htmlFor="page-size" className="block text-xs font-medium text-gray-500 mb-1">
                Per Page
              </label>
              <select
                id="page-size"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm bg-white"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            {(statusFilter !== 'all' || sourceFilter !== 'all' || searchQuery) && (
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setStatusFilter('all');
                    setSourceFilter('all');
                    setSearchQuery('');
                  }}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              <span className="ml-2">Loading leads...</span>
            </div>
          ) : unifiedLeads.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <UserPlus className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No leads found</p>
              <p className="text-sm">Leads will appear here when received from email captures or HighLevel webhooks</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Processed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {unifiedLeads.map((lead) => (
                    <tr key={`${lead.source}-${lead.id}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getSourceBadge(lead.source)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">
                            {lead.contact_info.firstName} {lead.contact_info.lastName}
                          </span>
                          <span className="text-xs text-gray-500">
                            {lead.contact_info.email || lead.contact_info.phone || 'No contact info'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(lead.status)}
                          {getStatusBadge(lead.status)}
                          {lead.retry_count && lead.retry_count > 0 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {lead.retry_count} retries
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(lead.created_at), 'MMM dd, yyyy HH:mm')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {lead.processed_at 
                          ? format(new Date(lead.processed_at), 'MMM dd, yyyy HH:mm')
                          : '-'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => setSelectedLead(lead)}
                          className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {totalCount > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages} ({totalCount.toLocaleString()} total leads)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1 || isLoading}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1 || isLoading}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {/* Page number buttons */}
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={isLoading}
                        className={`px-3 py-1.5 text-sm border rounded-md ${
                          currentPage === pageNum
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        } disabled:opacity-50`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || isLoading}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages || isLoading}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
} 